import { ClientListSummaryData, NestedContent, portfolioData, TableNestedGrouping } from "@models/dashboard/DashboardTables";
import * as moment from "moment";
import { FeesRepo } from "@services/fees.repo";
import { ClientRepo } from "@services/clients/client.repo";
import { extractErrorMessage } from "@common/utils/errorUtils";
import { Column } from "@models/dashboard/DashboardTables";

interface Client {
  id: number;
  clientNumber: string;
  name: string;
  displayText: string;
}

interface RelationshipManager {
  id: number;
  email: string;
  isActive: boolean;
  name: string;
}

class FullClientListSummaryController {
  public data: ClientListSummaryData[] = [];
  public isDaily: boolean = false;
  public fromDate;
  public toDate;
  public currentDate;
  public minDate; // 12 months ago - minimum selectable date
  public lastMonth;
  public isLoading: boolean = false;

  // Client name filter properties
  public clients: Array<any> = [];
  public selectedClients: Client[] = [];
  public relationshipManagerList: RelationshipManager[] = [];
  public selectedRelationshipManagers: RelationshipManager[] = [];
  public searchText: string = '';
  public clientName: string;
  public relationshipManagerSearchText: string = '';
  public isLoadingClients: boolean = false;

  public columns: Column[] = [
    { key: 'name', label: 'Name', type: 'text' },
    { key: 'clientNumber', label: 'Client Number', type: 'text' },
    { key: 'relationshipManager', label: 'Relationship Manager', type: 'text' },
  ];

  public nestedColumns: Column[] = [
    { key: 'dataGrouping', label: 'Portfolio Number' },
    { key: 'feeType', label: 'Fee Type', type: 'text' },
    { key: 'murNav', label: 'NAV in Mur', type: 'currency' },
    { key: 'navDelta', label: 'Nav Delta', type: 'percentage' },
    { key: 'murFees', label: 'Fees in Mur', type: 'currency' },
    { key: 'feeDelta', label: 'Fee Delta', type: 'percentage' },
    { key: 'currency', label: 'Currency', type: 'text' },
    { key: 'currencyNav', label: 'Nav in Reference Currency', type: 'currency' },
    { key: 'currencyFees', label: 'Fee in Reference Currency', type: 'currency' },
    { key: 'cashAccount', label: 'Debit Account', type: 'text' },
  ];

  /** @ngInject */
  constructor(
    private $scope: angular.IScope,
    private feesRepo: FeesRepo,
    private clientRepo: ClientRepo,
    private $window: angular.IWindowService,
    private notifyjs: any,
    private $element: angular.IAugmentedJQuery
  ) {
    this.currentDate = new Date();
    this.minDate = new Date(moment(this.currentDate).subtract(12, "months").startOf("month").format("YYYY-MM-DD"));
    this.fromDate = new Date(moment(this.currentDate).subtract(6, "months").startOf("month").format("YYYY-MM-DD"));
    this.toDate = new Date(moment(this.currentDate).subtract(1, "months").endOf("month").format("YYYY-MM-DD"));
    this.clampDatesToMinRange();
    this.loadClients();
    this.loadRelationshipManagers();
    this.fetchData();

    // Watch for isDaily changes and update toDate when switching to daily mode
    this.$scope.$watch(() => this.isDaily, (newValue: boolean, oldValue: boolean) => {
      if (newValue === true && oldValue === false && this.toDate) {
        // When switching to daily mode, set toDate to the end of the currently selected month
        this.toDate = new Date(moment(this.toDate).endOf("month").format("YYYY-MM-DD"));
      }
    });
  }

  formatDate(selectedDate) {
    return moment(selectedDate).format("YYYY-MM");
  }

  private clampDatesToMinRange(): void {
    if (this.minDate && this.fromDate && moment(this.fromDate).isBefore(moment(this.minDate))) {
      this.fromDate = new Date(this.minDate.getTime());
    }
    if (this.minDate && this.toDate && moment(this.toDate).isBefore(moment(this.minDate))) {
      this.toDate = new Date(this.fromDate.getTime());
    }
  }

  loadClients(): void {
    this.isLoadingClients = true;

    this.clientRepo.getClientsForSelection().then((response: any) => {
      const clientData = response.data as Array<Client>;
      this.clients = clientData.map((client: any) => ({
        id: client.id,
        name: client.displayName,
        clientNumber: client.clientNumber,
        portfolioAccountNumbers: client.portfolioAccountNumbers || [] // Include portfolio numbers for search
      }));

      this.clients.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    }).catch((error) => {
      this.notifyjs('Failed to load clients', 'error');
      console.error('Error loading clients:', error);
    })
    .finally(() => {
      this.isLoadingClients = false;
    });
  }

  loadRelationshipManagers(): void {
    this.clientRepo.getAllRelationshipManagers().then((response: any) => {
      this.relationshipManagerList = response.data as RelationshipManager[];
      this.relationshipManagerList.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    }).catch((error) => {
      this.notifyjs('Failed to load relationship managers', 'error');
      console.error('Error loading relationship managers:', error);
    });
  }

  clearSearchTerm(): void {
    this.searchText = '';
  }

  clearRelationshipManagerSearchTerm(): void {
    this.relationshipManagerSearchText = '';
  }

  clearNestedTableCache(): void {
    const nestedTableElement = this.$element.find('reusable-nested-table');
    if (nestedTableElement.length > 0) {
      const scope = nestedTableElement.isolateScope();
      if (scope && (scope as any).$ctrl && (scope as any).$ctrl.clearCache) {
        (scope as any).$ctrl.clearCache();
      }
    }
  }

  fetchData() {
    this.isLoading = true;
    let fromDate = moment(this.fromDate).startOf("month").format("YYYY-MM-DD");
    let toDate = moment(this.toDate).endOf("month").format("YYYY-MM-DD");

    if (this.isDaily) {
      fromDate = moment(this.fromDate).format("YYYY-MM-DD");
      toDate = moment(this.toDate).format("YYYY-MM-DD");
    }

    // Clear cached data in reusable-nested-table
    this.clearNestedTableCache();

    this.feesRepo
      .getClientFeesSummaryList(fromDate, toDate, this.isDaily, this.selectedClients.map(client => client.clientNumber), this.selectedRelationshipManagers.map(relationshipManager => relationshipManager.id))
      .then((response: any) => {
        this.data = response.data;
      })
      .catch((err) => {
        this.notifyjs("Failed to load client fees summary " + extractErrorMessage(err));
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getClientFeesSummary(client: Client): Promise<any[]> {
    if (!client) {
      console.error('Client is undefined');
      return Promise.resolve([]);
    }

    let clientNumber = client.clientNumber;

    let fromDate = moment(this.fromDate).startOf("month").format("YYYY-MM-DD");
    let toDate = moment(this.toDate).endOf("month").format("YYYY-MM-DD");

    if (this.isDaily) {
      fromDate = moment(this.fromDate).format("YYYY-MM-DD");
      toDate = moment(this.toDate).format("YYYY-MM-DD");
    }

    let promise = new Promise<any[]>((resolve, reject) => {
      this.feesRepo
        .getClientFeesSummary(fromDate, toDate, this.isDaily, clientNumber)
        .then((response: any) => {
          resolve(response.data || []);
        })
        .catch((err) => {
          reject(err);
        });
    });
    return promise;
  }

  private calculateDateRange(fromDateInput: Date | string, toDateInput: Date | string): { fromDate: string; toDate: string } {
    let fromDate: string;
    let toDate: string;

    if (this.isDaily) {
      fromDate = moment(fromDateInput).format("YYYY-MM-DD");
      toDate = moment(toDateInput).format("YYYY-MM-DD");
    } else {
      fromDate = moment(fromDateInput).startOf("month").format("YYYY-MM-DD");
      toDate = moment(toDateInput).endOf("month").format("YYYY-MM-DD");
    }

    return { fromDate, toDate };
  }

  private calculateDateRangeFromSingleDate(dateInput: Date | string): { fromDate: string; toDate: string } {
    let fromDate: string;
    let toDate: string;

    if (this.isDaily) {
      fromDate = moment(dateInput).startOf("day").format("YYYY-MM-DD");
      toDate = moment(dateInput).endOf("day").format("YYYY-MM-DD");
    } else {
      fromDate = moment(dateInput).startOf("month").format("YYYY-MM-DD");
      toDate = moment(dateInput).endOf("month").format("YYYY-MM-DD");
    }

    return { fromDate, toDate };
  }

  private downloadAuditExcel(fromDate: string, toDate: string, clientNumbers: string | string[], relationshipManagerIds: number | number[]): void {
    this.isLoading = true;

    this.feesRepo
      .getIndividualClientsAuditExcel(fromDate, toDate, this.isDaily, clientNumbers, relationshipManagerIds)
      .then((res) => {
        let auditExcel = res.data as any;
        this.$window.open(auditExcel.url, "_blank");
      })
      .catch((err) => {
        this.notifyjs(
          "Failed to generate audit excel " + (err.data || err.message || err)
        );
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  getAuditExcel = () => {
    const { fromDate, toDate } = this.calculateDateRange(this.fromDate, this.toDate);
    const clientNumbers = this.selectedClients.map(client => client.clientNumber);
    const relationshipManagerIds = this.selectedRelationshipManagers.map(relationshipManager => relationshipManager.id);
    this.downloadAuditExcel(fromDate, toDate, clientNumbers, relationshipManagerIds);
  };

  downloadClientFeesSummary(client: ClientListSummaryData): void {
    const { fromDate, toDate } = this.calculateDateRange(client.fromDate, client.toDate);
    const clientNumber = client.clientNumber;
    this.downloadAuditExcel(fromDate, toDate, clientNumber, []); // No relationship manager IDs for this method
  }

  downloadClientFeesSummaryByDate(client: ClientListSummaryData, nestedGrouping: TableNestedGrouping): void {
    const date = nestedGrouping.dataGrouping;
    const { fromDate, toDate } = this.calculateDateRangeFromSingleDate(date);
    const clientNumber = client.clientNumber;
    this.downloadAuditExcel(fromDate, toDate, clientNumber, []); // No relationship manager IDs for this method
  }
}

export const fullClientListSummary = {
  template: require("./full-client-list-summary.html"),
  style: require("./full-client-list-summary.scss"),
  controller: FullClientListSummaryController,
  bindings: {
    // Add bindings here as needed
  },
};




// WEBPACK FOOTER //
// ./src/app/fees/fees-components/full-client-list-summary/full-client-list-summary.ts